import { createElement, useMemo } from "react";
import { clsx } from "clsx";

import { definedProps } from "~/utils/components";

import "./Text.css";

import type { CSSProperties, ReactNode } from "react";
import type {
  LetterSpacingProp,
  LineHeightProp,
  SizeProp,
  WeightProp,
} from "~/components/components";

export interface TextProps {
  align?: "left" | "center" | "right";
  as?:
    | "div"
    | "label"
    | "legend"
    | "p"
    | "small"
    | "span"
    | "strong"
    | "h1"
    | "h2"
    | "h3"
    | "h4";
  children?: ReactNode;
  className?: string;
  color?: string;
  lineHeight?: LineHeightProp;
  preset?: "meta" | "pretitle" | "title" | "description";
  size?: SizeProp;
  spacing?: LetterSpacingProp;
  style?: CSSProperties;
  title?: string;
  transform?: "capitalize" | "uppercase" | "lowercase";
  weight?: WeightProp;
}

/**
 * Returns the props for the given preset.
 */
function getPresetProps(preset: TextProps["preset"]): Partial<TextProps> {
  switch (preset) {
    case "meta":
    case "pretitle":
      return {
        align: "left",
        color: "gray",
        size: "xs",
        weight: "semibold",
        lineHeight: "snug",
      };
    case "title":
      return {
        align: "left",
        color: "black",
        size: "md",
        weight: "medium",
        lineHeight: "snug",
      };
    case "description":
      return {
        align: "left",
        color: "gray",
        size: "sm",
        lineHeight: "snug",
      };
    default:
      return {};
  }
}

function getCustomProperties({
  align,
  color,
  lineHeight,
  size,
  spacing,
  transform,
  weight,
}: TextProps): CSSProperties {
  const customProperties = {} as Record<string, string>;

  if (align) {
    customProperties["--text-align"] = align;
  }

  if (color) {
    customProperties["--text-color"] = `var(--color-${color})`;
  }

  if (lineHeight) {
    customProperties["--text-line-height"] = `var(--line-height-${lineHeight})`;
  }

  if (size) {
    customProperties["--text-font-size"] = `var(--font-size-${size})`;
  }

  if (spacing) {
    customProperties["--text-letter-spacing"] =
      `var(--letter-spacing-${spacing})`;
  }

  if (transform) {
    customProperties["--text-transform"] = transform;
  }

  if (weight) {
    customProperties["--text-font-weight"] = `var(--font-weight-${weight})`;
  }

  return customProperties as CSSProperties;
}

export function Text({
  align,
  as = "div",
  children,
  className,
  color,
  lineHeight,
  preset,
  size,
  spacing,
  style,
  title,
  transform,
  weight,
}: TextProps) {
  const mergedStyles = useMemo(
    () => ({
      ...getCustomProperties({
        ...getPresetProps(preset),
        ...definedProps({
          align,
          color,
          lineHeight,
          size,
          spacing,
          transform,
          weight,
        }),
      }),
      ...style,
    }),
    [align, color, lineHeight, preset, size, spacing, style, transform, weight],
  );

  return createElement(
    as,
    {
      className: clsx("Text", className),
      style: mergedStyles,
      title,
    },
    children,
  );
}
